import React, { useEffect, useState } from "react";
import { Box, Divider, Typography, Grid } from "@mui/material";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import ByzSelectListPopupInput from "src/components/core-components/mui/inputs/ByzSelectListPopupInput";
import { referenceService } from "src/api/references/referenceService";
import { useAppSelector } from "src/app/hooks";
import { RootState } from "src/app/store";
import { WillOpenCardKey } from "src/api/request/requestService";
import { useTranslate } from "src/locales";

interface PregnantControlModalProps {
  open: boolean;
  onClose: () => void;
  onSave?: (pregnancyStatus: string) => void;
  // Merkezi additional-info fetch için opsiyonel parametreler
  additionalInfoKey?: WillOpenCardKey;
  additionalInfoParams?: {
    serviceId: number;
    requestDetailId: number;
    ptId?: number;
    officeId?: number;
  };
}

const PregnantControlModal: React.FC<PregnantControlModalProps> = ({
  open,
  onClose,
  onSave,
  additionalInfoParams,
}) => {
  const { t } = useTranslate();
  const [pregnancyInfo, setPregnancyInfo] = useState<string>("");
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  const { selectedOfficeId } = useAppSelector((state: RootState) => ({
    selectedOfficeId: state.auth.selectedOfficeId,
  }));
  const [additionalInfo, setAdditionalInfo] = useState<any>(null);

  useEffect(() => {
    if (open) {
      setPregnancyInfo("");
    }
  }, [open]);

  // WillOpenCard KEY bazlı ek bilgi formunu merkezi servisten yükle
  useEffect(() => {
    if (!open) return;

    const loadAdditionalInfo = async () => {
      try {
        // Form tipi backend sözleşmesine göre sabit: "PREGNANT_CONTROL"
        const res = await referenceService.additionalInfo.getFields({
          formtype: "PREGNANT_CONTROL",
          offid:
            additionalInfoParams?.officeId ??
            (selectedOfficeId ? Number(selectedOfficeId) : undefined),
        });
        if (res) {
          setAdditionalInfo(res);
        }


        // Dönen fields içinden seçenekleri kur
        const field = res.fields && res.fields.length > 0 ? res.fields[0] : null;
        if (field && Array.isArray(field.options)) {
          const mapped = field.options.map((opt) => ({
            value: String(opt.value),
            label: opt.label,
          }));
          setOptions(mapped);

          // İstersen ilk değeri otomatik seçebilirsin, şimdilik sadece mevcut state'i koruyoruz.
        } else {
          // Backend'den alan dönmezse, en azından eski sabit listeye düş
          setOptions([
            { value: "0", label: "Evet" },
            { value: "1", label: "Hayır" },
            { value: "2", label: "Şüpheli" },
          ]);
        }
      } catch {
        // Hata durumunda mevcut manuel akış devam eder.
        // Fallback olarak sabit seçenekleri kullan
        setOptions([
          { value: "0", label: "Evet" },
          { value: "1", label: "Hayır" },
          { value: "2", label: "Şüpheli" },
        ]);
      }
    };

    loadAdditionalInfo();
  }, [open, additionalInfoParams, selectedOfficeId]);

  const handleSave = () => {
    if (!pregnancyInfo) return;
    onSave?.(pregnancyInfo);
    if (!onSave) {
      onClose();
    }
  };

  return (
    <OpenComponentDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      hideHeader
      showPatientData={false}
    >
      <Box
        sx={{
          mx: -2,
          mt: 4,
          mb: 2,
          py: 1.5,
          px: 2,
          backgroundColor: "#F4F7FE",
          borderBottom: "1px solid #E0E0E0",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" fontWeight={700} color="#111827">
          {t("patient-request.pregnant-control.title")}
        </Typography>
      </Box>

      <Box
        sx={{
          px: { xs: 1, sm: 1.5 },
          pb: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
          }}
        >
          <ByzMuiSaveButton onClick={handleSave} disabled={!pregnancyInfo} />
          <ByzMuiCloseButton onClick={onClose} />
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t("patient-request.pregnant-control.pregnancy-info")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <ByzSelectListPopupInput
              fullWidth
              size="small"
              label={t("patient-request.pregnant-control.pregnancy-info")}
              value={
                options.find((opt) => opt.value === pregnancyInfo)?.label || ""
              }
              selectedId={pregnancyInfo || undefined}
              dialogMaxHeight="60vh"
              staticData={options.map((opt) => ({
                id: opt.value,
                NAME: opt.label,
              }))}
              displayField="NAME"
              onChange={(rows) => {
                const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                const first = list[0];
                if (first) {
                  setPregnancyInfo(String(first.id));
                }
              }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ mt: 3 }} />
      </Box>
    </OpenComponentDialog>
  );
};

export default PregnantControlModal;
